import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip
} from '@mui/material';
import { Logout, Visibility, Edit, Delete, Add, ArrowBack, Image } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const EventsManagement = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    titleAr: '',
    description: '',
    descriptionAr: '',
    date: '',
    time: '',
    location: '',
    locationAr: '',
    category: 'workshop',
    image: '',
    gallery: [],
    outcomes: [''],
    outcomesAr: [''],
    health: false,
    culture: false,
    sports: false,
    professional: false,
    internal: false,
    external: false,
    status: 'upcoming'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const imageInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [eventsPage, setEventsPage] = useState(1);
  const itemsPerPage = 10;

  // URL du serveur backend pour les images - utilise l'URL de l'API
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // Si c'est déjà une URL complète ou une URL blob, la retourner telle quelle
    if (imagePath.startsWith('http') || imagePath.startsWith('blob:') || imagePath.startsWith('/image')) {
      return imagePath;
    }
    // Détecter l'environnement basé sur le hostname au lieu de NODE_ENV
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const baseUrl = isProduction ? '' : 'http://localhost:3002';
    // Si c'est un chemin relatif, construire l'URL complète
    if (imagePath.startsWith('/uploads')) {
      return `${baseUrl}${imagePath}`;
    }
    // Par défaut, ajouter /uploads si ce n'est pas déjà présent
    return `${baseUrl}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
  };

  // Vérifier si un événement est passé en prenant en compte la date ET l'heure
  const isEventPast = (dateString, timeString) => {
    if (!dateString) return false;
    
    const eventDate = new Date(dateString);
    
    // Si l'heure est spécifiée, l'ajouter à la date de l'événement
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      eventDate.setHours(hours, minutes, 0, 0);
    } else {
      // Si pas d'heure spécifiée, utiliser 23:59 comme heure par défaut (fin de journée)
      eventDate.setHours(23, 59, 0, 0);
    }
    
    return eventDate < new Date();
  };
  
  // Mettre à jour le statut des événements en fonction de la date et de l'heure actuelles
  const updateEventsStatus = () => {
    if (events.length === 0) return;
    
    const currentTime = new Date();
    console.log(`Vérification du statut des événements à ${currentTime.toLocaleTimeString()}`);
    
    // Créer une copie des événements pour les mettre à jour
    const updatedEvents = events.map(event => {
      // Déterminer si l'événement est passé en fonction de la date et de l'heure actuelles
      const shouldBePast = isEventPast(event.date, event.time);
      
      // Si le statut doit changer, mettre à jour l'événement
      if (shouldBePast && event.status === 'upcoming') {
        console.log(`L'événement "${event.title}" est maintenant passé`);
        
        // Mettre à jour le statut dans la base de données
        api.updateEvent(event._id, { status: 'past' })
          .then(response => {
            console.log(`Statut de l'événement ${event.title} mis à jour dans la base de données`);
          })
          .catch(error => {
            console.error(`Erreur lors de la mise à jour du statut de l'événement ${event.title}:`, error);
          });
        
        return { ...event, status: 'past' };
      }
      
      return event;
    });
    
    // Mettre à jour l'état avec les événements mis à jour
    if (JSON.stringify(updatedEvents) !== JSON.stringify(events)) {
      console.log('Mise à jour du statut des événements');
      setEvents(updatedEvents);
    }
  };

  useEffect(() => {
    fetchEvents();
    
    // Mettre à jour le statut des événements toutes les minutes
    const statusUpdateInterval = setInterval(() => {
      updateEventsStatus();
    }, 60000); // 60000 ms = 1 minute
    
    return () => clearInterval(statusUpdateInterval);
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await api.getEvents();
      console.log('Événements récupérés:', response.data);
      
      // Formater les événements avec les URLs d'images complètes
      let eventsData = [];
      if (response.data && Array.isArray(response.data)) {
        eventsData = response.data;
      } else if (response.data && response.data.events && Array.isArray(response.data.events)) {
        eventsData = response.data.events;
      }
      
      // Ajouter les URLs complètes pour les images
      const formattedEvents = eventsData.map(event => ({
        ...event,
        image: getImageUrl(event.image),
        gallery: Array.isArray(event.gallery) 
          ? event.gallery.map(img => getImageUrl(img)) 
          : []
      }));
      
      setEvents(formattedEvents);
      
      // Vérifier et mettre à jour le statut des événements après les avoir chargés
      setTimeout(() => {
        updateEventsStatus();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la récupération des événements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      // Construire l'URL correcte pour l'image principale
      const imageUrl = getImageUrl(event.image);
      
      // Construire les URLs correctes pour les images de la galerie
      const galleryUrls = (event.gallery || []).map(img => getImageUrl(img));
      
      setFormData({
        title: event.title || '',
        titleAr: event.titleAr || '',
        description: event.description || '',
        descriptionAr: event.descriptionAr || '',
        date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
        time: event.time || '',
        location: event.location || '',
        locationAr: event.locationAr || '',
        category: event.category || 'workshop',
        image: imageUrl,
        gallery: galleryUrls,
        outcomes: event.outcomes || [''],
        outcomesAr: event.outcomesAr || [''],
        health: event.health || false,
        culture: event.culture || false,
        sports: event.sports || false,
        professional: event.professional || false,
        internal: event.internal || false,
        external: event.external || false,
        status: event.status || 'upcoming'
      });
      setSelectedEvent(event);
      setIsEditing(true);
      
      // Réinitialiser les fichiers
      setImageFile(null);
      setGalleryFiles([]);
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    } else {
      setFormData({
        title: '',
        titleAr: '',
        description: '',
        descriptionAr: '',
        date: '',
        time: '',
        location: '',
        locationAr: '',
        category: 'workshop',
        image: '',
        gallery: [],
        outcomes: [''],
        outcomesAr: [''],
        health: false,
        culture: false,
        sports: false,
        professional: false,
        internal: false,
        external: false,
        status: 'upcoming'
      });
      setSelectedEvent(null);
      setIsEditing(false);
      
      // Réinitialiser les fichiers
      setImageFile(null);
      setGalleryFiles([]);
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
  };

  const handleOpenDeleteDialog = (event) => {
    setSelectedEvent(event);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedEvent(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOutcomeChange = (index, value, isArabic = false) => {
    const field = isArabic ? 'outcomesAr' : 'outcomes';
    const newOutcomes = [...formData[field]];
    newOutcomes[index] = value;
    setFormData(prev => ({
      ...prev,
      [field]: newOutcomes
    }));
  };

  const addOutcome = (isArabic = false) => {
    const field = isArabic ? 'outcomesAr' : 'outcomes';
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeOutcome = (index, isArabic = false) => {
    const field = isArabic ? 'outcomesAr' : 'outcomes';
    const newOutcomes = [...formData[field]];
    newOutcomes.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      [field]: newOutcomes
    }));
  };

  const handleGalleryChange = (index, value) => {
    const newGallery = [...formData.gallery];
    newGallery[index] = value;
    setFormData(prev => ({
      ...prev,
      gallery: newGallery
    }));
  };

  const addGalleryImage = () => {
    setFormData(prev => ({
      ...prev,
      gallery: [...prev.gallery, '']
    }));
  };

  const removeGalleryImage = (index) => {
    // Créer une copie des tableaux
    const newGallery = [...formData.gallery];
    const newGalleryFiles = [...galleryFiles];
    
    // Supprimer l'élément à l'index spécifié
    newGallery.splice(index, 1);
    if (index < newGalleryFiles.length) {
      newGalleryFiles.splice(index, 1);
    }
    
    // Mettre à jour l'état
    setFormData(prev => ({
      ...prev,
      gallery: newGallery
    }));
    setGalleryFiles(newGalleryFiles);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('L\'image est trop volumineuse. La taille maximale est de 5MB.');
        return;
      }
      
      setImageFile(file);
      // Créer une URL temporaire pour l'aperçu
      const imageUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        image: imageUrl
      }));
      
      console.log('Image sélectionnée:', file.name, 'taille:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
    }
  };

  const handleGalleryFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      
      // Vérifier la taille de chaque fichier (max 5MB)
      const validFiles = newFiles.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          console.warn(`L'image ${file.name} est trop volumineuse et ne sera pas ajoutée.`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length === 0) {
        alert('Aucune image n\'a été ajoutée. Toutes les images dépassent la taille maximale de 5MB.');
        return;
      }
      
      if (validFiles.length < newFiles.length) {
        alert('Certaines images dépassent la taille maximale de 5MB et n\'ont pas été ajoutées.');
      }
      
      setGalleryFiles(prev => [...prev, ...validFiles]);
      
      // Créer des URLs temporaires pour l'aperçu
      const newImageUrls = validFiles.map(file => URL.createObjectURL(file));
      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, ...newImageUrls]
      }));
      
      console.log(`${validFiles.length} images ajoutées à la galerie`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Déterminer le statut en fonction de la date
      const eventDate = new Date(formData.date);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Réinitialiser l'heure pour comparer uniquement les dates
      const status = eventDate < currentDate ? 'past' : 'upcoming';
      
      // Ajouter les champs de texte
      Object.keys(formData).forEach(key => {
        if (key !== 'image' && key !== 'gallery') {
          if (key === 'status') {
            // Utiliser le statut calculé au lieu de celui du formulaire
            formDataToSend.append('status', status);
          } else if (typeof formData[key] === 'boolean') {
            // Gérer correctement les valeurs booléennes
            formDataToSend.append(key, formData[key]);
          } else if (Array.isArray(formData[key])) {
            // Gérer correctement les tableaux (comme outcomes)
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else {
            // Ajouter la valeur réelle du formulaire
            formDataToSend.append(key, formData[key] || '');
          }
        }
      });
      
      console.log('Données du formulaire envoyées:', Object.fromEntries(formDataToSend));
      
      // Ajouter l'image principale si elle existe
      if (imageFile) {
        console.log('Ajout de l\'image principale:', imageFile.name);
        formDataToSend.append('image', imageFile);
      }
      
      // Ajouter les images de la galerie si elles existent
      if (galleryFiles.length > 0) {
        console.log(`Ajout de ${galleryFiles.length} nouvelles images de galerie`);
        galleryFiles.forEach(file => {
          formDataToSend.append('gallery', file);
        });
      } else if (formData.gallery && formData.gallery.length > 0) {
        // Si pas de nouveaux fichiers mais des URLs existantes, les envoyer comme JSON
        formDataToSend.append('gallery', JSON.stringify(formData.gallery));
      }

      console.log('Envoi des données:', isEditing ? 'Mise à jour' : 'Création');
      
      // Log des données du formulaire pour le débogage
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value instanceof File ? `Fichier: ${value.name}` : value}`);
      }

      let response;
      if (isEditing && selectedEvent) {
        // Si on édite un événement existant
        response = await api.updateEvent(selectedEvent._id, formDataToSend);
      } else {
        // Si on crée un nouvel événement
        response = await api.createEvent(formDataToSend);
      }

      console.log('Réponse du serveur:', response.data);
      
      // Réinitialiser le formulaire et rafraîchir la liste des événements
      setFormData({
        title: '',
        titleAr: '',
        description: '',
        descriptionAr: '',
        date: '',
        time: '',
        location: '',
        locationAr: '',
        category: 'workshop',
        image: '',
        gallery: [],
        outcomes: [''],
        outcomesAr: [''],
        health: false,
        culture: false,
        sports: false,
        professional: false,
        internal: false,
        external: false,
        status: 'upcoming'
      });
      setImageFile(null);
      setGalleryFiles([]);
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      fetchEvents();
      setOpenDialog(false);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'événement:', error);
      console.log('Détails de l\'erreur:', error.response?.data || error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      await api.deleteEvent(selectedEvent._id);
      // Rafraîchir la liste des événements
      fetchEvents();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'workshop': return 'Atelier';
      case 'conference': return 'Conférence';
      case 'seminar': return 'Séminaire';
      case 'training': return 'Formation';
      default: return category;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming': return 'À venir';
      case 'past': return 'Passé';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'primary';
      case 'past': return 'default';
      default: return 'default';
    }
  };

  // Pagination logic for events
  const totalEventsPages = Math.ceil(events.length / itemsPerPage);
  const paginatedEvents = events.slice(
    (eventsPage - 1) * itemsPerPage,
    eventsPage * itemsPerPage
  );

  const renderImagePreview = () => {
    if (formData.image) {
      // Utiliser getImageUrl pour obtenir l'URL correcte
      const imageUrl = getImageUrl(formData.image);
      
      console.log('Image preview URL:', imageUrl);
      
      return (
        <Box sx={{ mt: 2, position: 'relative' }}>
          <Box
            component="img"
            sx={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid #ddd'
            }}
            src={imageUrl}
            alt="Prévisualisation"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/image/logofr.png';
            }}
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
            size="small"
            onClick={() => {
              setFormData(prev => ({ ...prev, image: null }));
              setImageFile(null);
              // Si c'était une URL blob, libérer la mémoire
              if (formData.image && formData.image.startsWith('blob:')) {
                URL.revokeObjectURL(formData.image);
              }
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: '#f5f5f5'
    }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton 
            color="inherit" 
            edge="start" 
            onClick={handleBackToDashboard}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gestion des Événements
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Liste des Événements</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Ajouter un événement
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titre</TableCell>
                    <TableCell>Titre (Arabe)</TableCell>
                    <TableCell>Catégorie</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Lieu</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEvents.length > 0 ? (
                    paginatedEvents.map((event) => (
                      <TableRow key={event._id}>
                        <TableCell>{event.title}</TableCell>
                        <TableCell>{event.titleAr || '-'}</TableCell>
                        <TableCell>{getCategoryLabel(event.category)}</TableCell>
                        <TableCell>{new Date(event.date).toLocaleDateString()}</TableCell>
                        <TableCell>{event.location}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(event.status)} 
                            color={getStatusColor(event.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleOpenDialog(event)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog(event)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Aucun événement trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {events.length > 0 && (
            <Pagination
              currentPage={eventsPage}
              totalPages={totalEventsPages}
              onPageChange={setEventsPage}
              itemsPerPage={itemsPerPage}
              totalItems={events.length}
            />
          )}
        </Paper>
      </Container>

      {/* Dialogue d'ajout/modification d'événement */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Modifier un événement' : 'Ajouter un événement'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Informations en français</Typography>
                <TextField
                  name="title"
                  label="Titre"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                  multiline
                  rows={4}
                />
                <TextField
                  name="location"
                  label="Lieu"
                  value={formData.location}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Résultats attendus</Typography>
                {formData.outcomes.map((outcome, index) => (
                  <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={outcome}
                      onChange={(e) => handleOutcomeChange(index, e.target.value)}
                      fullWidth
                      placeholder={`Résultat ${index + 1}`}
                    />
                    <IconButton color="error" onClick={() => removeOutcome(index)}>
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button 
                  variant="outlined" 
                  startIcon={<Add />} 
                  onClick={() => addOutcome()}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Ajouter un résultat
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Informations en arabe</Typography>
                <TextField
                  name="titleAr"
                  label="Titre (Arabe)"
                  value={formData.titleAr}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  dir="rtl"
                />
                <TextField
                  name="descriptionAr"
                  label="Description (Arabe)"
                  value={formData.descriptionAr}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  dir="rtl"
                />
                <TextField
                  name="locationAr"
                  label="Lieu (Arabe)"
                  value={formData.locationAr}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  dir="rtl"
                />
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Résultats attendus (Arabe)</Typography>
                {formData.outcomesAr.map((outcome, index) => (
                  <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={outcome}
                      onChange={(e) => handleOutcomeChange(index, e.target.value, true)}
                      fullWidth
                      placeholder={`Résultat ${index + 1} (Arabe)`}
                      dir="rtl"
                    />
                    <IconButton color="error" onClick={() => removeOutcome(index, true)}>
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button 
                  variant="outlined" 
                  startIcon={<Add />} 
                  onClick={() => addOutcome(true)}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Ajouter un résultat (Arabe)
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="date"
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="time"
                  label="Heure"
                  type="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Catégorie et Tags</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="category-select-label">Catégorie</InputLabel>
                      <Select
                        labelId="category-select-label"
                        name="category"
                        value={formData.category}
                        label="Catégorie"
                        onChange={handleInputChange}
                      >
                        <MenuItem value="workshop">Atelier</MenuItem>
                        <MenuItem value="conference">Conférence</MenuItem>
                        <MenuItem value="seminar">Séminaire</MenuItem>
                        <MenuItem value="training">Formation</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
                      Le statut de l'événement (À venir/Passé) est déterminé automatiquement en fonction de la date.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Sélectionnez les tags applicables :</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.health} onChange={handleInputChange} name="health" />}
                          label="Santé"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.culture} onChange={handleInputChange} name="culture" />}
                          label="Culture"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.sports} onChange={handleInputChange} name="sports" />}
                          label="Sports"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.professional} onChange={handleInputChange} name="professional" />}
                          label="Professionnel"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.internal} onChange={handleInputChange} name="internal" />}
                          label="Interne"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={<Checkbox checked={formData.external} onChange={handleInputChange} name="external" />}
                          label="Externe"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Image principale</Typography>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange} 
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  id="main-image-upload"
                />
                <label htmlFor="main-image-upload">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component="span"
                    startIcon={<Image />}
                  >
                    Télécharger l'image principale
                  </Button>
                </label>
                {renderImagePreview()}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Images de la galerie</Typography>
                <input 
                  type="file" 
                  accept="image/*" 
                  multiple 
                  onChange={handleGalleryFilesChange} 
                  ref={galleryInputRef}
                  style={{ display: 'none' }}
                  id="gallery-images-upload"
                />
                <label htmlFor="gallery-images-upload">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component="span"
                    startIcon={<Image />}
                  >
                    Télécharger les images de la galerie
                  </Button>
                </label>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  {formData.gallery && formData.gallery.length > 0 && formData.gallery.map((image, index) => (
                    image && (
                      <Box key={index} sx={{ position: 'relative', width: 200 }}>
                        <img 
                          src={image} 
                          alt={`Image de la galerie ${index + 1}`} 
                          style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/image/logofr.png';
                          }}
                        />
                        <IconButton 
                          color="error" 
                          onClick={() => removeGalleryImage(index)}
                          sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'rgba(255,255,255,0.7)' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )
                  ))}
                </Box>
              </Grid>
              {/* La section Tags a été déplacée et regroupée avec la section Catégorie */}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              {isEditing ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'événement "{selectedEvent?.title}" ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDeleteEvent} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsManagement;
